import { onLocaleChange, t } from "../i18n/index.js";
import { el } from "./dom.js";
import { vigilBackdrop } from "./vigil.js";

/** Shipped from client/public, so it is a plain URL and not a bundled asset. */
const SOURCE = "/begin.mp4";

/**
 * How long to wait for the first frame before giving up on the film.
 *
 * On 4G the file streams fine, but a stalled download must not hold a fan at a
 * black screen on the way to a memorial. Cleared the moment it plays.
 */
const FIRST_FRAME_TIMEOUT_MS = 8000;

/**
 * Anything that counts as user activation. The first one turns the sound on.
 *
 * Not "focusin": tab-focusing an element is not user activation, so unmuting
 * there would be refused and we would have burned the listener for nothing.
 */
const GESTURES = ["pointerdown", "touchstart", "keydown"] as const;

/**
 * The size of the canvas the spill is sampled into, and how often.
 *
 * Deliberately tiny. Anything bigger is detail that the blur is about to throw
 * away anyway, paid for on every sample; anything faster is a redraw of a
 * heavily blurred layer that nobody can see changing.
 */
const SPILL_W = 16;
const SPILL_H = 28;
const SPILL_EVERY_MS = 100;

/** Wide enough for the frame to have a room around it worth lighting. */
const SPILL_QUERY = window.matchMedia("(min-width: 521px)");

/**
 * The opening film, shown once before the gate.
 *
 * The film wants sound. It asks for it outright, and where the browser allows
 * that — a desktop the fan has visited before, a kiosk started with the policy
 * off — the film simply starts with sound and nobody has to do anything.
 *
 * Where the browser refuses, and on a phone opening a shared link it always
 * refuses, there is no page-side override: unmuted playback without a gesture
 * is not ours to grant. So the film starts silent and the *first touch
 * anywhere on the screen* turns the sound on — not a hunt for a small button,
 * any tap at all, which is what someone watching a tribute does within a
 * second or two anyway. The toggle stays as the visible hint and the way back
 * to silence, using the same wording as the venue's own sound button.
 *
 * Every failure lands on the same exit as the skip button: refused playback, a
 * codec the phone will not take, a download that never arrives. Nobody is ever
 * left looking at a black rectangle with no way past it.
 */
export class IntroVideo {
  private readonly root: HTMLDivElement;
  private readonly video: HTMLVideoElement;
  private readonly controls: HTMLDivElement;
  private readonly skipButton: HTMLButtonElement;
  private readonly soundButton: HTMLButtonElement;
  private readonly credit: HTMLParagraphElement;
  private readonly frame: HTMLDivElement;
  /** The light the film throws on the room. Null where it is not worth drawing. */
  private readonly spill: HTMLCanvasElement | null;
  private spillTimer = 0;
  private readonly unsubscribe: () => void;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") this.finish();
  };
  /** Removes the waiting-for-a-gesture listeners. Replaced once they are armed. */
  private disarmSound: () => void = () => {};
  private timer = 0;
  private done = false;
  private played = false;

  /**
   * @param onDone Called once the gate is clear. `played` is false when the
   *   film never ran at all — a browser that refused autoplay in a background
   *   tab tells us nothing about whether anyone is actually here.
   */
  constructor(
    private readonly parent: HTMLElement,
    private readonly onDone: (played: boolean) => void,
  ) {
    this.video = el("video", {
      className: "zg-intro-video",
      attrs: {
        src: SOURCE,
        preload: "auto",
        playsinline: "",
        "webkit-playsinline": "",
        // The two buttons are the control surface; the frame itself is not.
        tabindex: "-1",
      },
    });
    // No autoplay attribute: start() drives playback itself, so the fall back
    // from sound to silence is ours to make rather than the browser's.
    //
    // Muted for the first paint only — the button must never promise sound
    // before the browser has agreed to it. start() asks for it immediately.
    this.video.muted = true;
    this.video.addEventListener("ended", () => this.finish());
    this.video.addEventListener("error", () => this.finish());
    this.video.addEventListener("playing", () => {
      this.played = true;
      window.clearTimeout(this.timer);
      this.startSpill();
    });
    /*
     * Cut the frame to the film rather than guessing.
     *
     * The stylesheet starts at 9:16, which is what this film happens to be, but
     * a frame whose shape is hard-coded to one file is a frame that letterboxes
     * the day somebody swaps the file for a landscape one. The real ratio
     * arrives with the metadata, well before the first frame does.
     */
    this.video.addEventListener("loadedmetadata", () => {
      const { videoWidth: w, videoHeight: h } = this.video;
      if (w > 0 && h > 0) this.root.style.setProperty("--zg-intro-ar", String(w / h));
    });

    this.skipButton = el("button", { className: "zg-intro-skip", attrs: { type: "button" } });
    this.skipButton.addEventListener("click", () => this.finish());

    this.soundButton = el("button", { className: "zg-intro-sound", attrs: { type: "button" } });
    this.soundButton.addEventListener("click", () => this.toggleSound());

    /*
     * The credit for the film.
     *
     * Above the controls rather than beside them, because the row below is
     * already a sound toggle at one end and a way out at the other, and a
     * third thing in it would be read as a third button. Small and dim: it is
     * an attribution, and it is on top of somebody's footage of a crowd.
     */
    this.credit = el("p", { className: "zg-intro-credit" });

    this.controls = el("div", {
      className: "zg-intro-controls",
      children: [this.soundButton, this.skipButton],
    });

    /*
     * The film is a FRAMED thing, not a fill.
     *
     * It is shot portrait, so on any screen wider than it is tall the browser
     * letterboxed it into the middle and left the rest black: a 405px strip of
     * concert in eight hundred pixels of nothing, with the two buttons flung
     * into opposite corners of the viewport, a clear foot away from the film
     * they belonged to.
     *
     * So the film gets a frame cut to its own aspect ratio, with the night and
     * the lamplight of the other two screens behind it instead of dead black,
     * and everything that belongs to the film — the credit, the sound, the way
     * out — sits inside that frame on a scrim. Nothing is stranded, and the
     * width the film does not use is a dark room rather than a void.
     */
    const scrim = el("div", {
      className: "zg-intro-scrim",
      children: [this.credit, this.controls],
    });

    this.frame = el("div", {
      className: "zg-intro-frame",
      children: [this.video, scrim],
    });

    /*
     * The light the film throws on the room.
     *
     * A screen playing in the dark lights what is around it, and the surround
     * here was staying the same dead navy through a concert full of stage
     * lights. This is a sixteen-by-twenty-eight-pixel canvas — smaller than the
     * favicon — redrawn from the film ten times a second and then blown up
     * behind the frame under a heavy blur. At that size the draw costs nothing
     * and all that survives of the image is its colour, which is the only part
     * wanted: the wall goes red when the rig does.
     *
     * Only where there is a wall to light. On a phone the frame runs edge to
     * edge, so there is nothing to spill onto and this would be a blurred
     * full-screen layer recomposited ten times a second for no visible result.
     */
    this.spill = SPILL_QUERY.matches
      ? el("canvas", {
          className: "zg-intro-spill",
          attrs: { width: String(SPILL_W), height: String(SPILL_H), "aria-hidden": "true" },
        })
      : null;

    this.root = el("div", {
      className: "zg-intro",
      children: [
        vigilBackdrop(),
        el("div", {
          className: "zg-intro-stage",
          children: [
            el("div", { className: "zg-intro-screen", children: [this.spill, this.frame] }),
          ],
        }),
      ],
    });

    this.parent.appendChild(this.root);
    this.applyStrings();
    this.unsubscribe = onLocaleChange(() => this.applyStrings());
    window.addEventListener("keydown", this.onKeyDown);

    void this.start();
    this.timer = window.setTimeout(() => this.finish(), FIRST_FRAME_TIMEOUT_MS);
  }

  /**
   * Play with sound if the browser allows it, silently if that is all it will
   * give us, and give up if it will not play at all.
   */
  private async start(): Promise<void> {
    // Sound outright. This wins on a desktop the fan has visited before.
    if (await this.tryPlay(false)) return;

    // Refused, so take the silent play and wait for the first touch to earn
    // the sound back. Waiting silently beats not playing at all: a fan who
    // taps in the first second hears all but that second of the film.
    if (await this.tryPlay(true)) {
      this.armSoundOnFirstGesture();
      return;
    }

    // Refused even in silence: this is not the autoplay policy any more, it is
    // a phone that will not play the file. There is nothing to ask the fan for.
    this.finish();
  }

  /** Sample the film into the spill canvas until the film is over. */
  private startSpill(): void {
    if (!this.spill || this.spillTimer) return;
    const context = this.spill.getContext("2d", { alpha: false });
    if (!context) return;

    this.spillTimer = window.setInterval(() => {
      // HAVE_CURRENT_DATA. Drawing a video with no frame in it throws, and a
      // stalled download is exactly when that happens.
      if (this.video.readyState < 2) return;
      try {
        context.drawImage(this.video, 0, 0, SPILL_W, SPILL_H);
      } catch {
        // Nothing to be done and nothing worth saying: the room simply keeps
        // the colour it had. Stop asking.
        this.stopSpill();
      }
    }, SPILL_EVERY_MS);
  }

  private stopSpill(): void {
    window.clearInterval(this.spillTimer);
    this.spillTimer = 0;
  }

  /** Turn the sound on at the first gesture the browser will accept. */
  private armSoundOnFirstGesture(): void {
    const unmute = (event: Event): void => {
      // The controls answer for themselves. Without this, a tap on the sound
      // button would unmute here and then be muted again a moment later by the
      // button's own click — the one button that must work, not working.
      if (event.target instanceof Node && this.controls.contains(event.target)) return;
      this.disarmSound();
      void this.raiseSound();
    };

    for (const name of GESTURES) {
      // Capture, so a handler that stops propagation cannot cost the fan the
      // sound — and passive, because this must never delay a scroll or a tap.
      window.addEventListener(name, unmute, { capture: true, passive: true });
    }

    this.disarmSound = () => {
      this.disarmSound = () => {};
      for (const name of GESTURES) {
        window.removeEventListener(name, unmute, { capture: true });
      }
    };
  }

  /**
   * Ask for sound. If it is still refused — a gesture the browser did not
   * count, an older policy — the film keeps playing silently rather than
   * freezing on the frame where the sound was denied.
   */
  private async raiseSound(): Promise<void> {
    if (await this.tryPlay(false)) return;
    if (await this.tryPlay(true)) return;
    this.finish();
  }

  /** Play at the given mute setting. False when the browser refused. */
  private async tryPlay(muted: boolean): Promise<boolean> {
    this.video.muted = muted;
    try {
      await this.video.play();
      this.applyStrings();
      return true;
    } catch {
      return false;
    }
  }

  private applyStrings(): void {
    this.skipButton.textContent = t("intro.skip");
    this.soundButton.textContent = t(this.video.muted ? "sound.off" : "sound.on");
    this.soundButton.classList.toggle("is-on", !this.video.muted);
    this.credit.textContent = t("intro.credit");
    this.video.setAttribute("aria-label", t("intro.label"));
  }

  private toggleSound(): void {
    // Either way this settles it: silence chosen here must not be undone by
    // the next tap anywhere on the screen.
    this.disarmSound();
    // The tap is also the gesture the policy was holding out for, so raising
    // the sound here is what rescues a film that never started.
    void (this.video.muted ? this.raiseSound() : this.tryPlay(true));
  }

  /** Skipped, ended, or failed — all three land here, exactly once. */
  private finish(): void {
    if (this.done) return;
    this.done = true;
    window.clearTimeout(this.timer);
    this.stopSpill();
    window.removeEventListener("keydown", this.onKeyDown);
    this.disarmSound();
    this.root.classList.add("is-hidden");

    // Cut the download dead. Whatever is left of a 7 MB file is bytes the fan
    // has just told us they do not want to pay for.
    this.video.pause();
    this.video.removeAttribute("src");
    this.video.load();

    this.onDone(this.played);
    window.setTimeout(() => this.destroy(), 400);
  }

  destroy(): void {
    this.unsubscribe();
    this.stopSpill();
    window.clearTimeout(this.timer);
    window.removeEventListener("keydown", this.onKeyDown);
    this.disarmSound();
    this.root.remove();
  }
}
